/**
 * Dashboard Tour Module - Interactive tour of portal features
 *
 * Features:
 * - Step-by-step walkthrough of dashboard sections
 * - Interactive highlights and tooltips
 * - Role-aware feature highlighting
 * - Completion tracking
 */

import { useState, useCallback } from 'react';
import {
  LayoutDashboard,
  CheckSquare,
  FileText,
  BarChart3,
  MessageSquare,
  Settings,
  Bell,
  Search,
  HelpCircle,
  ChevronRight,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Calendar,
  Users,
  FolderOpen,
} from 'lucide-react';
import type { OnboardingRole } from '../../types/onboarding';
import { StepCard } from './OnboardingStep';
import { cn } from '../../lib/utils';

interface DashboardTourModuleProps {
  onComplete: () => void;
  role: OnboardingRole;
}

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  features: string[];
  tip?: string;
  roles?: OnboardingRole[];
}

export default function DashboardTourModule({
  onComplete,
  role,
}: DashboardTourModuleProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState<string[]>([]);

  // Filter tour steps based on role
  const tourSteps = TOUR_STEPS.filter(
    (step) => !step.roles || step.roles.includes(role)
  );

  const currentTourStep = tourSteps[currentStep];

  // Handle step navigation
  const handleNext = useCallback(() => {
    // Mark current step as visited
    if (!visitedSteps.includes(currentTourStep.id)) {
      setVisitedSteps((prev) => [...prev, currentTourStep.id]);
    }

    if (currentStep < tourSteps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, currentTourStep, tourSteps.length, visitedSteps]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const handleStepClick = useCallback((index: number) => {
    setCurrentStep(index);
  }, []);

  const handleComplete = useCallback(() => {
    // Mark final step as visited if not already
    if (!visitedSteps.includes(currentTourStep.id)) {
      setVisitedSteps((prev) => [...prev, currentTourStep.id]);
    }
    onComplete();
  }, [currentTourStep, visitedSteps, onComplete]);

  const Icon = currentTourStep.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <StepCard variant="highlight">
        <div className="flex items-start gap-4">
          <LayoutDashboard
            className="w-10 h-10 flex-shrink-0"
            style={{ color: 'var(--color-accent)' }}
          />
          <div>
            <h3
              className="font-bold text-xl mb-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Dashboard Tour
            </h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Let's walk through the key features of your Team Portal dashboard.
              This tour will help you navigate and use the platform effectively.
            </p>
          </div>
        </div>
      </StepCard>

      {/* Progress Dots */}
      <div className="flex items-center justify-center gap-2">
        {tourSteps.map((step, index) => (
          <button
            key={step.id}
            onClick={() => handleStepClick(index)}
            className={cn(
              'w-2.5 h-2.5 rounded-full transition-all',
              'hover:scale-125'
            )}
            style={{
              backgroundColor:
                index === currentStep
                  ? 'var(--color-accent)'
                  : visitedSteps.includes(step.id)
                  ? 'var(--color-success)'
                  : 'var(--color-bg-tertiary)',
            }}
            aria-label={`Go to ${step.title}`}
          />
        ))}
      </div>

      {/* Current Step Content */}
      <div
        className="p-6 rounded-xl animate-in fade-in slide-in-from-right-4 duration-300"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
        }}
        key={currentTourStep.id}
      >
        {/* Step Header */}
        <div className="flex items-center gap-4 mb-6">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-accent-muted)' }}
          >
            <Icon className="w-7 h-7" style={{ color: 'var(--color-accent)' }} />
          </div>
          <div>
            <span
              className="text-xs font-medium"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Step {currentStep + 1} of {tourSteps.length}
            </span>
            <h4
              className="text-lg font-bold"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {currentTourStep.title}
            </h4>
          </div>
        </div>

        {/* Description */}
        <p
          className="text-sm mb-6"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {currentTourStep.description}
        </p>

        {/* Features List */}
        <div className="space-y-3 mb-6">
          {currentTourStep.features.map((feature, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-3 rounded-lg"
              style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
            >
              <CheckCircle2
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                style={{ color: 'var(--color-success)' }}
              />
              <span
                className="text-sm"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {feature}
              </span>
            </div>
          ))}
        </div>

        {/* Pro Tip */}
        {currentTourStep.tip && (
          <div
            className="p-4 rounded-lg flex items-start gap-3"
            style={{
              backgroundColor: 'var(--color-accent-muted)',
              borderLeft: '3px solid var(--color-accent)',
            }}
          >
            <Sparkles
              className="w-5 h-5 flex-shrink-0"
              style={{ color: 'var(--color-accent)' }}
            />
            <div>
              <span
                className="text-xs font-semibold block mb-1"
                style={{ color: 'var(--color-accent)' }}
              >
                Pro Tip
              </span>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {currentTourStep.tip}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrev}
          disabled={currentStep === 0}
          className="px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            color: 'var(--color-text-secondary)',
          }}
        >
          Previous
        </button>

        {/* Step indicator */}
        <span
          className="text-sm"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {visitedSteps.length} of {tourSteps.length} viewed
        </span>

        {currentStep < tourSteps.length - 1 ? (
          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-all hover:scale-105"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'var(--color-bg)',
            }}
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleComplete}
            className="flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-all hover:scale-105"
            style={{
              backgroundColor: 'var(--color-success)',
              color: 'white',
            }}
          >
            Complete Tour
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Quick Reference Card */}
      <div
        className="p-4 rounded-xl"
        style={{
          backgroundColor: 'var(--color-bg-tertiary)',
          border: '1px solid var(--color-border)',
        }}
      >
        <h5
          className="font-semibold mb-3 text-sm"
          style={{ color: 'var(--color-text-primary)' }}
        >
          Quick Reference
        </h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {tourSteps.slice(0, 8).map((step, index) => {
            const StepIcon = step.icon;
            const isVisited = visitedSteps.includes(step.id);
            const isCurrent = index === currentStep;

            return (
              <button
                key={step.id}
                onClick={() => handleStepClick(index)}
                className={cn(
                  'p-3 rounded-lg text-center transition-all',
                  'hover:scale-105'
                )}
                style={{
                  backgroundColor: isCurrent
                    ? 'var(--color-accent-muted)'
                    : 'var(--color-bg-secondary)',
                  border: isCurrent
                    ? '2px solid var(--color-accent)'
                    : '2px solid transparent',
                }}
              >
                <div className="flex items-center justify-center gap-1 mb-1">
                  <StepIcon
                    className="w-4 h-4"
                    style={{
                      color: isVisited
                        ? 'var(--color-success)'
                        : isCurrent
                        ? 'var(--color-accent)'
                        : 'var(--color-text-muted)',
                    }}
                  />
                  {isVisited && (
                    <CheckCircle2
                      className="w-3 h-3"
                      style={{ color: 'var(--color-success)' }}
                    />
                  )}
                </div>
                <span
                  className="text-xs block"
                  style={{
                    color: isCurrent
                      ? 'var(--color-accent)'
                      : 'var(--color-text-muted)',
                  }}
                >
                  {step.title}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Tour Steps Data
// ============================================================================

const TOUR_STEPS: TourStep[] = [
  {
    id: 'overview',
    title: 'Dashboard Overview',
    description:
      'Your dashboard is your home base. It provides a quick summary of your tasks, notifications, and team activity.',
    icon: LayoutDashboard,
    features: [
      'Activity feed showing recent updates from your team',
      'Quick stats showing your tasks and deadlines',
      'Personalized widgets based on your role',
      'One-click access to frequently used features',
    ],
    tip: 'Customize your dashboard layout in Settings to show the widgets most relevant to your work.',
  },
  {
    id: 'tasks',
    title: 'Tasks & Projects',
    description:
      'Manage your work with our task management system. Create, assign, and track tasks with ease.',
    icon: CheckSquare,
    features: [
      'Create tasks with descriptions, due dates, and priorities',
      'Organize tasks into projects and milestones',
      'Track progress with status updates and comments',
      'Filter and sort by assignee, status, or due date',
    ],
    tip: 'Use keyboard shortcuts (press "?" on any page) to quickly create tasks and navigate between views.',
  },
  {
    id: 'docs',
    title: 'Documents & Knowledge Base',
    description:
      'Access team documentation, policies, and shared knowledge. Everything you need is organized and searchable.',
    icon: FileText,
    features: [
      'Browse organized document folders',
      'Full-text search across all documents',
      'Version history for all changes',
      'Share documents securely with team members',
    ],
    tip: 'Star frequently accessed documents for quick access from your dashboard.',
  },
  {
    id: 'reports',
    title: 'Reports & Analytics',
    description:
      'View insights and analytics about team performance, project progress, and system metrics.',
    icon: BarChart3,
    features: [
      'Pre-built reports for common metrics',
      'Custom report builder for specific needs',
      'Export data in multiple formats',
      'Scheduled report delivery',
    ],
    tip: 'Set up automated weekly reports to stay informed without manual checking.',
    roles: ['internal_core', 'research_collab'],
  },
  {
    id: 'messaging',
    title: 'Team Communication',
    description:
      'Stay connected with your team through integrated messaging and collaboration tools.',
    icon: MessageSquare,
    features: [
      'Direct messages with team members',
      'Channel-based discussions for projects',
      'Thread conversations for organized discussions',
      'File and document sharing in context',
    ],
    tip: 'Mention @team or @channel to notify everyone, or @username for specific people.',
  },
  {
    id: 'calendar',
    title: 'Calendar & Scheduling',
    description:
      'Manage your schedule and coordinate meetings with integrated calendar features.',
    icon: Calendar,
    features: [
      'Personal and team calendar views',
      'Meeting scheduling with availability checking',
      'Integration with task due dates',
      'Reminders and notifications',
    ],
    tip: 'Sync with your external calendar (Google, Outlook) in Settings for unified scheduling.',
  },
  {
    id: 'files',
    title: 'File Management',
    description:
      'Upload, organize, and share files securely within the platform.',
    icon: FolderOpen,
    features: [
      'Drag-and-drop file uploads',
      'Folder organization and tagging',
      'Preview common file types in-browser',
      'Secure sharing with access controls',
    ],
    tip: 'Use tags to organize files by project or topic for easier searching.',
  },
  {
    id: 'team',
    title: 'Team Directory',
    description:
      'Find and connect with team members across the organization.',
    icon: Users,
    features: [
      'Searchable team directory',
      'Contact information and roles',
      'Organizational structure view',
      'Quick message or email options',
    ],
    tip: 'Click on any team member\'s avatar throughout the app to view their profile.',
    roles: ['internal_core', 'research_collab', 'contractor'],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description:
      'Stay updated with customizable notifications for the things that matter to you.',
    icon: Bell,
    features: [
      'Real-time notifications for mentions and assignments',
      'Daily digest option for less urgent updates',
      'Customizable notification preferences',
      'Email, in-app, and mobile notification options',
    ],
    tip: 'Set quiet hours in Settings to avoid notifications during focus time or after hours.',
  },
  {
    id: 'search',
    title: 'Global Search',
    description:
      'Find anything across the platform with powerful search capabilities.',
    icon: Search,
    features: [
      'Search across tasks, documents, messages, and more',
      'Filter results by type, date, or author',
      'Recent searches for quick access',
      'Advanced search operators for power users',
    ],
    tip: 'Press Cmd+K (Mac) or Ctrl+K (Windows) from anywhere to open quick search.',
  },
  {
    id: 'settings',
    title: 'Settings & Preferences',
    description:
      'Personalize your experience with themes, notifications, and account settings.',
    icon: Settings,
    features: [
      'Light, dark, and system theme options',
      'Notification preferences per category',
      'Profile and account management',
      'Connected integrations and API access',
    ],
    tip: 'Enable "Reduce Motion" in Accessibility settings if you prefer a calmer interface.',
  },
  {
    id: 'help',
    title: 'Help & Support',
    description:
      'Get help when you need it through documentation, tutorials, and support channels.',
    icon: HelpCircle,
    features: [
      'In-app help documentation',
      'Video tutorials for common tasks',
      'Support ticket submission',
      'Community forums and FAQs',
    ],
    tip: 'Click the "?" icon in the bottom right of any page for contextual help.',
  },
];
